/**
 * src/apiError.js
 *
 * Dependency-free core of the API transport: the normalized `ApiError`, the
 * generic per-status message table, and the `normalize` / `runFetch` helpers.
 *
 * Kept import-free (no MSAL, no `import.meta.env`, no browser globals at module
 * load) so this pure logic is unit-testable in a plain Node environment. The
 * MSAL-aware `authedJson` / `publicJson` wrappers live in apiClient.js.
 */

const GENERIC_BY_STATUS = {
  400: "The request was invalid. Please check your input and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: "That change conflicts with the current state. Please refresh and try again.",
};
export const GENERIC_FALLBACK = "Something went wrong. Please try again.";
export const NETWORK_MESSAGE  = "Can't reach the server. Check your connection and try again.";

/** Normalized, display-safe error for every failed API call. */
export class ApiError extends Error {
  constructor({ message, code = null, correlationId = null, status = 0 }) {
    super(message || GENERIC_FALLBACK);
    this.name = "ApiError";
    this.code = code;
    this.correlationId = correlationId;
    this.status = status;
  }
}

export function genericForStatus(status) {
  if (status >= 500) return GENERIC_FALLBACK;
  return GENERIC_BY_STATUS[status] || GENERIC_FALLBACK;
}

/**
 * Turn a fetch Response into parsed JSON, or throw a normalized ApiError.
 * OK 204 / empty body → null. OK non-JSON body → the raw text.
 */
export async function normalize(res) {
  if (res.ok) {
    if (res.status === 204) return null;
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return text; }
  }

  // Non-OK: read the sanitized body if present. Only `error` (a clean message),
  // `code`, and `correlationId` are ever trusted from it.
  let body = {};
  try { body = JSON.parse(await res.text()); } catch { /* leave empty */ }

  throw new ApiError({
    message:       (typeof body.error === "string" && body.error) || genericForStatus(res.status),
    code:          body.code || null,
    correlationId: body.correlationId || null,
    status:        res.status,
  });
}

/** Await a fetch promise, converting a network/transport failure into an ApiError. */
export async function runFetch(fetchPromise) {
  let res;
  try {
    res = await fetchPromise;
  } catch {
    throw new ApiError({ message: NETWORK_MESSAGE, code: "NETWORK", status: 0 });
  }
  return normalize(res);
}
